// frontend/src/components/admin/WalletTransactionTable.js

import React, { useState, useEffect } from 'react';
import { 
  MoreVertical, 
  Eye, 
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Wallet,
  Search,
  X
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const WalletTransactionTable = ({ 
  transactions, 
  pagination, 
  onView, 
  onRefresh,
  onPageChange,
  onSearch,
  searchTerm = '',
  isLoading = false 
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(pagination?.limit || 10);
  
  // Local search state with debounce
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Sync with parent search term
  useEffect(() => {
    if (searchTerm !== localSearchTerm) {
      setLocalSearchTerm(searchTerm || '');
    }
  }, [searchTerm]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (onSearch) {
        onSearch(value);
      }
    }, 500);
    
    setSearchTimeout(timeout);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(localSearchTerm);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      SUCCESS: { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle, label: 'Success' },
      PENDING: { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock, label: 'Pending' },
      FAILED: { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle, label: 'Failed' },
      REVERSED: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: RefreshCw, label: 'Reversed' },
    };
    
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${config.color}`}>
        <Icon className="h-2.5 w-2.5 mr-0.5" />
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (type === 'CREDIT') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
          <ArrowUpRight className="h-2.5 w-2.5 mr-0.5" />
          Credit
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
        <ArrowDownRight className="h-2.5 w-2.5 mr-0.5" />
        Debit
      </span>
    );
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const handlePageChange = (newPage) => {
    if (onPageChange && newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      onPageChange(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    if (onPageChange) {
      onPageChange(1, newLimit);
    }
  };

  const total = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = (currentPage - 1) * (pagination?.limit || 10) + 1;
  const endIndex = Math.min(currentPage * (pagination?.limit || 10), total);

  return (
    <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
      {/* Search Bar */}
      <div className="px-3 py-2 border-b border-primary-100/50 bg-gray-50/50">
        <div className="flex flex-col sm:flex-row items-center gap-2">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search wallet transactions by user or reference..."
              value={localSearchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-8 pr-8 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
            />
            {localSearchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center space-x-1.5 flex-shrink-0">
            <span className="text-[10px] text-gray-500">
              {total > 0 ? `${total} transactions found` : 'No transactions found'}
            </span>
            {isLoading && (
              <RefreshCw className="h-3.5 w-3.5 text-primary-500 animate-spin" />
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-primary-100/50 text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-primary-100/50">
            {isLoading ? (
              <tr>
                <td colSpan="7" className="px-3 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="h-4 w-4 text-primary-500 animate-spin" />
                    <span className="text-xs text-gray-500">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-3 py-8 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 shadow-neomorph-inset mb-3">
                    <Wallet className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 font-medium">No wallet transactions found</p>
                </td>
              </tr>
            ) : (
              transactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className={`p-1.5 rounded-lg ${
                        transaction.type === 'CREDIT' ? 'bg-green-50' : 'bg-red-50'
                      }`}>
                        {transaction.type === 'CREDIT' ? (
                          <ArrowUpRight className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <ArrowDownRight className="h-3.5 w-3.5 text-red-600" />
                        )}
                      </div>
                      <div className="ml-2">
                        <div className="text-xs font-medium text-gray-800">
                          #{transaction.reference?.slice(0, 12)}...
                        </div>
                        <div className="text-[10px] text-gray-500 truncate max-w-xs">
                          {transaction.description || 'Wallet transaction'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {transaction.user ? (
                      <div className="flex items-center">
                        <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center flex-shrink-0">
                          {transaction.user.avatar ? (
                            <img 
                              src={transaction.user.avatar} 
                              alt={transaction.user.firstName}
                              className="h-6 w-6 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="text-white font-semibold text-[10px]">
                              {transaction.user.firstName?.[0]}{transaction.user.lastName?.[0]}
                            </span>
                          )}
                        </div>
                        <div className="ml-2 min-w-0">
                          <div className="text-xs font-medium text-gray-800 truncate">
                            {transaction.user.firstName} {transaction.user.lastName}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {transaction.user.phone}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">N/A</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getTypeBadge(transaction.type)}
                    {transaction.linkedTransaction && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        Linked: {transaction.linkedTransactionType}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className={`text-xs font-semibold ${
                      transaction.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'CREDIT' ? '+' : '-'}{formatAmount(transaction.amount)}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                    {formatDate(transaction.createdAt)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap relative">
                    <button
                      onClick={() => {
                        setSelectedId(transaction.id);
                        setShowActions(!showActions);
                      }}
                      className="p-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
                    >
                      <MoreVertical className="h-3.5 w-3.5 text-gray-600" />
                    </button>
                    
                    {selectedId === transaction.id && showActions && (
                      <div className="absolute right-0 mt-1 w-48 bg-[#E8ECF5] rounded-xl shadow-neomorph border border-primary-100/30 z-10 overflow-hidden">
                        <button
                          onClick={() => {
                            if (onView) onView(transaction);
                            setShowActions(false);
                            setSelectedId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-white transition-all"
                        >
                          <div className="p-1 bg-blue-50 rounded-lg mr-2">
                            <Eye className="h-3 w-3 text-blue-600" />
                          </div>
                          View
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {!isLoading && transactions.length > 0 && (
        <div className="px-3 py-2 border-t border-primary-100/50 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center space-x-1.5">
              <label className="text-[10px] text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-0.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-[10px]"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-[10px] text-gray-500">per page</span>
            </div>

            <div className="text-[10px] text-gray-600">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span>
            </div>

            <div className="flex items-center space-x-0.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-6 h-6 rounded-lg text-[10px] font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletTransactionTable;